// MapJsonConverter converts the displayed map into JSON and vice-
// versa. The JSON string created corresponds to the schema expected
// by the iOS/macOS game.

import { InstructionType } from 'app/src/engine/Instruction';
import { MovableItemOrientation } from 'app/src/engine/movable-items/MovableItem';
import ConveyorTile, {
	ConveyorTileArrow,
	tileName as ConveyorTileName,
} from 'app/src/engine/tiles/ConveyorTile';
import EmptyFloorTile, {
	EmptyFloorTileOrientation,
	EmptyFloorTileType,
	tileName as EmptyFloorTileName,
} from 'app/src/engine/tiles/EmptyFloorTile';
import GearTile, {
	GearTileDirection,
	GearTileFloorOrientation,
	GearTileFloorType,
	tileName as GearTileName,
} from 'app/src/engine/tiles/GearTile';
import PitTile, {
	PitTileOrientation,
	PitTileType,
	tileName as PitTileName,
} from 'app/src/engine/tiles/PitTile';
import { TileWallType } from 'app/src/engine/tiles/TileWalls';
import { LevelDescription } from 'app/src/LevelDescription';

type RawData = {
	[key: string]: any;
};

const CURRENT_MAP_SCHEMA_VERSION = 2;

export default class LevelDataBuilder {
	public static sharedInstance = new LevelDataBuilder();

	public build(levelDescription: LevelDescription): RawData {
		const map: any = {};

		map.schemaVersion = CURRENT_MAP_SCHEMA_VERSION;

		map.name = levelDescription.title;
		map.description = levelDescription.description;

		// Initialize plugins. We'll fill them in as we build the grid and robots.
		const pluginMap: any = {};

		// Build the grid.
		map.grid = [];

		for (let row = 0; row < levelDescription.tiles.length; row++) {
			map.grid.push([]);

			// eslint-disable-next-line
			for (let col = 0; col < levelDescription.tiles[row].length; col++) {
				const tile = levelDescription.tiles[row][col];

				const gridEntry: any = {};

				if (tile instanceof EmptyFloorTile) {
					pluginMap.EmptyFloorGamePlugin = true;

					gridEntry.tile = EmptyFloorTileName;

					switch (tile.type) {
						case EmptyFloorTileType.Type1:
							gridEntry.type = 'Type1';
							break;
						case EmptyFloorTileType.Type2:
							gridEntry.type = 'Type2';
							break;
						case EmptyFloorTileType.Type3:
							gridEntry.type = 'Type3';
							break;
						case EmptyFloorTileType.Type4:
							gridEntry.type = 'Type4';
							break;
						default:
							throw new Error('Unexpected type!');
					}

					switch (tile.orientation) {
						case EmptyFloorTileOrientation.Up:
							gridEntry.orientation = 'Up';
							break;
						case EmptyFloorTileOrientation.Right:
							gridEntry.orientation = 'Right';
							break;
						case EmptyFloorTileOrientation.Down:
							gridEntry.orientation = 'Down';
							break;
						case EmptyFloorTileOrientation.Left:
							gridEntry.orientation = 'Left';
							break;
						default:
							throw new Error('Unexpected orientation!');
					}
				} else if (tile instanceof PitTile) {
					pluginMap.PitsGamePlugin = true;

					gridEntry.tile = PitTileName;

					switch (tile.type) {
						case PitTileType.Type1_1of1:
							gridEntry.name = 'PitTile-T1-1of1';
							break;
						default:
							throw new Error('Unexpected pit type!');
					}

					switch (tile.orientation) {
						case PitTileOrientation.Up:
							gridEntry.orientation = 'Up';
							break;
						case PitTileOrientation.Right:
							gridEntry.orientation = 'Right';
							break;
						case PitTileOrientation.Down:
							gridEntry.orientation = 'Down';
							break;
						case PitTileOrientation.Left:
							gridEntry.orientation = 'Left';
							break;
					}
				} else if (tile instanceof GearTile) {
					pluginMap.GearsGamePlugin = true;

					gridEntry.tile = GearTileName;

					gridEntry.direction =
						tile.direction === GearTileDirection.Clockwise ? 'Clockwise' : 'CounterClockwise';

					switch (tile.floorType) {
						case GearTileFloorType.Type1:
							gridEntry.floorType = 'Type1';
							break;
						case GearTileFloorType.Type2:
							gridEntry.floorType = 'Type2';
							break;
						case GearTileFloorType.Type3:
							gridEntry.floorType = 'Type3';
							break;
						case GearTileFloorType.Type4:
							gridEntry.floorType = 'Type4';
							break;
						default:
							throw new Error('Unexpected floor type!');
					}

					switch (tile.floorOrientation) {
						case GearTileFloorOrientation.Up:
							gridEntry.floorOrientation = 'Up';
							break;
						case GearTileFloorOrientation.Right:
							gridEntry.floorOrientation = 'Right';
							break;
						case GearTileFloorOrientation.Down:
							gridEntry.floorOrientation = 'Down';
							break;
						case GearTileFloorOrientation.Left:
							gridEntry.floorOrientation = 'Left';
							break;
						default:
							throw new Error('Unexpected floor orientation!');
					}
				} else if (tile instanceof ConveyorTile) {
					pluginMap.ConveyorsGamePlugin = true;

					gridEntry.tile = ConveyorTileName;

					switch (tile.arrow) {
						case ConveyorTileArrow.Left:
							gridEntry.arrow = 'Left';
							break;
						case ConveyorTileArrow.Up:
							gridEntry.arrow = 'Up';
							break;
						case ConveyorTileArrow.Right:
							gridEntry.arrow = 'Right';
							break;
						case ConveyorTileArrow.Down:
							gridEntry.arrow = 'Down';
							break;
						case ConveyorTileArrow.LeftFromTop:
							gridEntry.arrow = 'LeftFromTop';
							break;
						case ConveyorTileArrow.LeftFromBottom:
							gridEntry.arrow = 'LeftFromBottom';
							break;
						case ConveyorTileArrow.UpFromLeft:
							gridEntry.arrow = 'UpFromLeft';
							break;
						case ConveyorTileArrow.UpFromRight:
							gridEntry.arrow = 'UpFromRight';
							break;
						case ConveyorTileArrow.RightFromTop:
							gridEntry.arrow = 'RightFromTop';
							break;
						case ConveyorTileArrow.RightFromBottom:
							gridEntry.arrow = 'RightFromBottom';
							break;
						case ConveyorTileArrow.DownFromLeft:
							gridEntry.arrow = 'DownFromLeft';
							break;
						case ConveyorTileArrow.DownFromRight:
							gridEntry.arrow = 'DownFromRight';
							break;
						case ConveyorTileArrow.LeftAndFromTop:
							gridEntry.arrow = 'LeftAndFromTop';
							break;
						case ConveyorTileArrow.LeftAndFromBottom:
							gridEntry.arrow = 'LeftAndFromBottom';
							break;
						case ConveyorTileArrow.UpAndFromLeft:
							gridEntry.arrow = 'UpAndFromLeft';
							break;
						case ConveyorTileArrow.UpAndFromRight:
							gridEntry.arrow = 'UpAndFromRight';
							break;
						case ConveyorTileArrow.RightAndFromTop:
							gridEntry.arrow = 'RightAndFromTop';
							break;
						case ConveyorTileArrow.RightAndFromBottom:
							gridEntry.arrow = 'RightAndFromBottom';
							break;
						case ConveyorTileArrow.DownAndFromLeft:
							gridEntry.arrow = 'DownAndFromLeft';
							break;
						case ConveyorTileArrow.DownAndFromRight:
							gridEntry.arrow = 'DownAndFromRight';
							break;
						case ConveyorTileArrow.MergeLeft:
							gridEntry.arrow = 'MergeLeft';
							break;
						case ConveyorTileArrow.MergeUp:
							gridEntry.arrow = 'MergeUp';
							break;
						case ConveyorTileArrow.MergeRight:
							gridEntry.arrow = 'MergeRight';
							break;
						case ConveyorTileArrow.MergeDown:
							gridEntry.arrow = 'MergeDown';
							break;
						default:
							throw new Error('Unexpected arrow!');
					}
				} else {
					throw new Error('Unexpected tile type!');
				}

				if (tile.walls.size) {
					gridEntry.walls = [];

					// eslint-disable-next-line
					for (const wall of Array.from(tile.walls)) {
						switch (wall) {
							case TileWallType.Top:
								gridEntry.walls.push('Top');
								break;
							case TileWallType.Right:
								gridEntry.walls.push('Right');
								break;
							case TileWallType.Bottom:
								gridEntry.walls.push('Bottom');
								break;
							case TileWallType.Left:
								gridEntry.walls.push('Left');
								break;
						}
					}
				}

				map.grid[row].push(gridEntry);
			}
		}

		// Build robots.
		const robot = levelDescription.robots.size ? Array.from(levelDescription.robots)[0] : null;
		const robotLocation = (robot && levelDescription.robotLocations.get(robot)) || null;
		if (robotLocation) {
			pluginMap.RobotsGamePlugin = true;

			let orientationString = 'Up';
			switch (robotLocation.orientation) {
				case MovableItemOrientation.Up:
					orientationString = 'Up';
					break;
				case MovableItemOrientation.Right:
					orientationString = 'Right';
					break;
				case MovableItemOrientation.Down:
					orientationString = 'Down';
					break;
				case MovableItemOrientation.Left:
					orientationString = 'Left';
					break;
			}

			map.robots = [
				{
					id: 'Robot1',
					movableItem: 'BetaRobot',
					location: {
						row: robotLocation.row,
						col: robotLocation.col,
						orientation: orientationString,
					},
				},
			];
		}

		// Handle stars.
		map.stars = Array.from(levelDescription.stars).map((star) => star.location);

		// Handle instructions.
		const instructions: any = {
			Move1: levelDescription.instructionCounts.get(InstructionType.Move1),
			Move2: levelDescription.instructionCounts.get(InstructionType.Move2),
			Move3: levelDescription.instructionCounts.get(InstructionType.Move3),
			BackUp: levelDescription.instructionCounts.get(InstructionType.BackUp),
			TurnLeft: levelDescription.instructionCounts.get(InstructionType.TurnLeft),
			TurnRight: levelDescription.instructionCounts.get(InstructionType.TurnRight),
			UTurn: levelDescription.instructionCounts.get(InstructionType.UTurn),
			Halt: levelDescription.instructionCounts.get(InstructionType.Halt),
		};

		Object.keys(instructions).forEach((key) => {
			if (instructions[key] === 0) {
				delete instructions[key];
			}
		});

		map.instructions = instructions;

		// Add plugins.
		map.plugins = Object.keys(pluginMap);

		// Return the object.
		return map;
	}
}
